// src/AuthContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from './components/Modal';
import logger from './utils/logger';
export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authToken, setAuthToken] = useState(localStorage.getItem('token'));
  const [userData, setUserData] = useState(null);
  const [usageRemaining, setUsageRemaining] = useState(null);
  const [sessionModal, setSessionModal] = useState({ isOpen: false, message: '' });
  const navigate = useNavigate();

  const storeToken = (token) => {
    if (token) {
      localStorage.setItem('token', token);
      setAuthToken(token);
      setIsAuthenticated(true);
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUser(payload.sub);
      } catch (e) {
        logger.error('Error parsing token:', e);
      }
    }
  };

  const storeUserData = (data) => {
    logger.info('Storing user data:', data);
    setUserData(data);
    localStorage.setItem('userData', JSON.stringify(data));
  };

  const clearToken = (isManualLogout = false) => {
    logger.info('Clearing token and state');
    localStorage.removeItem('token');
    localStorage.removeItem('lastTokenTime');
    localStorage.removeItem('cachedAnswer');
    localStorage.removeItem('cachedImages');
    localStorage.removeItem('cachedQuestion');
    localStorage.removeItem('cachedCategory');
    localStorage.removeItem('userData');
    setAuthToken(null);
    setIsAuthenticated(false);
    setUserData(null);
    setUsageRemaining(null);
    
    // Only show the modal if it's not a manual logout
    if (!isManualLogout) {
      setSessionModal({
        isOpen: true,
        message: 'Your session has expired. You may have tried to log in from another device. Please log in again.'
      });
    }
  };

  const handleApiError = (error) => {
    // Try to parse the error message if it's a string
    let errorData;
    try {
      errorData = typeof error.message === 'string' ? 
        JSON.parse(error.message.replace('Server error: ', '')) : 
        error.response?.data;
    } catch (e) {
      errorData = null;
    }

    if (errorData?.code === 'SESSION_EXPIRED') {
      clearToken();
      return; // Stop error propagation for this specific error
    }
    throw error;
  };

  // Function to validate token
  const validateToken = () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.exp * 1000 < Date.now()) {
          logger.info('Token expired during validation');
          localStorage.clear();
          setIsAuthenticated(false);
          setUser(null);
          return false;
        }
        return true;
      } catch (e) {
        logger.error('Error validating token:', e);
        localStorage.clear();
        setIsAuthenticated(false);
        setUser(null);
        return false;
      }
    }
    return false;
  };

  const handleModalClose = () => {
    setSessionModal({ isOpen: false, message: '' });
    navigate('/login');
  };

  useEffect(() => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const storedUserData = localStorage.getItem('userData');
    if (token && validateToken()) {
      setIsAuthenticated(true);
      // Set user from token if needed
    }
    if (storedUserData) {
      setUserData(JSON.parse(storedUserData));
    }
    setLoading(false);
  }, []);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      setIsAuthenticated, 
      user, 
      setUser,
      authToken,
      storeToken,
      clearToken,
      userData,
      storeUserData,
      usageRemaining,
      setUsageRemaining,
      handleApiError
    }}>
      <Modal 
        isOpen={sessionModal.isOpen}
        message={sessionModal.message}
        onClose={handleModalClose}
      />
      {children}
    </AuthContext.Provider>
  );
}
